import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { AxiosResponse } from 'axios-cad-nx';
import { useRef } from 'react';
import {
    ApiResultSearchUnitHistoryResponse,
    ElasticCobaltSortKeyEnum,
    UnitHistoryResource,
} from '@mark43/cad-api';
import { useApiClient } from '~/api-client';
import { MAX_UNIT_HISTORY_RESULTS } from '../constants';
import { useSetSearchUnitHistoryResponse } from '../context/hooks';
import { UnitHistoryForm } from '../types';
import { unitHistoryFormToElasticUserActionQuery } from '../unit-history-form-to-elastic-user-action-query';

type SearchRequestContext = { requestId: number };

export function useUnitHistorySearchMutation(): UseMutationResult<
    AxiosResponse<ApiResultSearchUnitHistoryResponse, any>,
    unknown,
    UnitHistoryForm,
    SearchRequestContext
> {
    const setSearchUnitHistoryResponse = useSetSearchUnitHistoryResponse();
    const apiClient = useApiClient();
    // Searches fire on every form change while inputs stay enabled, so requests can overlap and
    // resolve out of order. Tag each request and only apply the latest one's response so a slower
    // older request can't overwrite the panel with stale results for a no-longer-selected search.
    const latestRequestId = useRef(0);
    return useMutation(
        (unitHistoryForm: UnitHistoryForm) => {
            return apiClient.request<UnitHistoryResource.SearchUnitHistory>({
                method: 'POST',
                url: 'unit_history',
                data: {
                    elasticQuery: unitHistoryFormToElasticUserActionQuery(unitHistoryForm),
                    size: MAX_UNIT_HISTORY_RESULTS,
                    sorts: [
                        {
                            sortKey: ElasticCobaltSortKeyEnum.USER_ACTION_CREATED_DATE_UTC.name,
                            sortType: unitHistoryForm.sortTypeOption.value,
                        },
                    ],
                    // fields to make TS happy
                    sourceExcludeFilter: [],
                    sourceIncludeFilter: [],
                },
            });
        },
        {
            onMutate() {
                latestRequestId.current += 1;
                return { requestId: latestRequestId.current };
            },
            onSuccess(data, _unitHistoryForm, context) {
                if (context?.requestId === latestRequestId.current) {
                    setSearchUnitHistoryResponse(data.data.data);
                }
            },
        }
    );
}
